import { useMemo, useState } from 'react';
import modalStyles from './Modal.module.css';
import styles from './BulkSwapQuotaModal.module.css';

function formatQuotaOption(quota) {
  const name = quota.quota_name?.trim() || `Quota #${quota.cnq_id}`;
  return `${name} (cnq_id ${quota.cnq_id}, type ${quota.cnq_type})`;
}

function collectFromOptions(users) {
  const map = new Map();

  users.forEach((user) => {
    (user.quota_data ?? []).forEach((quota) => {
      if (quota?.cnq_id == null) return;
      const key = Number(quota.cnq_id);
      const existing = map.get(key);
      if (existing) {
        existing.userCount += 1;
        return;
      }
      map.set(key, {
        cnq_id: key,
        quota_name: quota.quota_name,
        cnq_type: quota.cnq_type,
        userCount: 1,
      });
    });
  });

  return [...map.values()].sort((a, b) => {
    const nameCmp = String(a.quota_name ?? '').localeCompare(String(b.quota_name ?? ''));
    return nameCmp || a.cnq_id - b.cnq_id;
  });
}

function collectToOptions(users, fromCnqId, fromType) {
  const map = new Map();
  const fromId = Number(fromCnqId);

  users.forEach((user) => {
    const hasFrom = (user.quota_data ?? []).some((quota) => Number(quota.cnq_id) === fromId);
    if (!hasFrom) return;

    const candidates = user.editable_quotas?.length
      ? user.editable_quotas
      : user.quota_data ?? [];

    candidates.forEach((quota) => {
      if (quota?.cnq_id == null) return;
      const id = Number(quota.cnq_id);
      if (id === fromId) return;
      if (fromType != null && quota.cnq_type != null && Number(quota.cnq_type) !== Number(fromType)) {
        return;
      }
      if (quota.is_full && !quota.is_selected) return;

      const existing = map.get(id);
      if (existing) {
        existing.userCount += 1;
        return;
      }
      map.set(id, {
        cnq_id: id,
        quota_name: quota.quota_name,
        cnq_type: quota.cnq_type,
        userCount: 1,
      });
    });
  });

  return [...map.values()].sort((a, b) => {
    const nameCmp = String(a.quota_name ?? '').localeCompare(String(b.quota_name ?? ''));
    return nameCmp || a.cnq_id - b.cnq_id;
  });
}

export default function BulkSwapQuotaModal({ users, onClose, onConfirm }) {
  const [fromCnqId, setFromCnqId] = useState('');
  const [toCnqId, setToCnqId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fromOptions = useMemo(() => collectFromOptions(users), [users]);

  const selectedFrom = useMemo(
    () => fromOptions.find((quota) => String(quota.cnq_id) === String(fromCnqId)) ?? null,
    [fromOptions, fromCnqId],
  );

  const toOptions = useMemo(
    () => (selectedFrom ? collectToOptions(users, selectedFrom.cnq_id, selectedFrom.cnq_type) : []),
    [users, selectedFrom],
  );

  const eligibleCount = selectedFrom
    ? users.filter((user) =>
        (user.quota_data ?? []).some((quota) => Number(quota.cnq_id) === Number(selectedFrom.cnq_id)),
      ).length
    : 0;

  const handleFromChange = (value) => {
    setFromCnqId(value);
    setToCnqId('');
    setError('');
  };

  const handleConfirm = async () => {
    if (!fromCnqId || !toCnqId) {
      setError('Select both a source and target quota.');
      return;
    }
    if (Number(fromCnqId) === Number(toCnqId)) {
      setError('Source and target quota must be different.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await onConfirm({
        wh_mo_ids: users.map((user) => user.wh_mo_id),
        from_cnq_id: Number(fromCnqId),
        to_cnq_id: Number(toCnqId),
      });
    } catch (e) {
      setError(e.message || 'Failed to swap quota.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={modalStyles.overlay} onClick={onClose}>
      <div
        className={modalStyles.panel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bulk-swap-title"
      >
        <div className={modalStyles.header}>
          <h2 id="bulk-swap-title" className={modalStyles.title}>
            Bulk Swap Quota
          </h2>
          <div className={modalStyles.idPill}>
            <span className={modalStyles.idLabel}>selected</span>
            <span className={modalStyles.idValue}>{users.length}</span>
          </div>
          <button type="button" className={modalStyles.closeBtn} onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className={`${modalStyles.body} ${styles.body}`}>
          <p className={styles.hint}>
            Replace one assigned quota with another across the selected users. Only users that
            currently have the source <code>cnq_id</code> will be updated.
          </p>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="bulk-from-quota">
              From quota (cnq_id)
            </label>
            <select
              id="bulk-from-quota"
              className={styles.select}
              value={fromCnqId}
              onChange={(e) => handleFromChange(e.target.value)}
            >
              <option value="">Select source quota…</option>
              {fromOptions.map((quota) => (
                <option key={quota.cnq_id} value={quota.cnq_id}>
                  {formatQuotaOption(quota)} · {quota.userCount} user
                  {quota.userCount !== 1 ? 's' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label className={styles.label} htmlFor="bulk-to-quota">
              To quota (cnq_id)
            </label>
            <select
              id="bulk-to-quota"
              className={styles.select}
              value={toCnqId}
              onChange={(e) => {
                setToCnqId(e.target.value);
                setError('');
              }}
              disabled={!selectedFrom}
            >
              <option value="">
                {selectedFrom ? 'Select target quota…' : 'Select a source quota first…'}
              </option>
              {toOptions.map((quota) => (
                <option key={quota.cnq_id} value={quota.cnq_id}>
                  {formatQuotaOption(quota)}
                  {quota.userCount > 1 ? ` · available for ${quota.userCount}` : ''}
                </option>
              ))}
            </select>
            {selectedFrom && toOptions.length === 0 && (
              <p className={styles.fieldHint}>
                No alternate quotas of type {selectedFrom.cnq_type} found on the selected users.
                Filter to the same cell / quota group, or use Re-assign Quota per user.
              </p>
            )}
          </div>

          {selectedFrom && (
            <div className={styles.summary}>
              <p>
                Will attempt swap on <strong>{eligibleCount}</strong> of{' '}
                <strong>{users.length}</strong> selected users that have cnq_id{' '}
                <code>{selectedFrom.cnq_id}</code>
                {toCnqId ? (
                  <>
                    {' '}
                    → <code>{toCnqId}</code>
                  </>
                ) : null}
                .
              </p>
            </div>
          )}

          {error && <div className={modalStyles.inlineError}>{error}</div>}
        </div>

        <div className={modalStyles.footer}>
          <button type="button" className={modalStyles.cancelBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.swapBtn}
            onClick={handleConfirm}
            disabled={saving || !fromCnqId || !toCnqId}
          >
            {saving ? 'Swapping…' : 'Swap Quota'}
          </button>
        </div>
      </div>
    </div>
  );
}
